/**
 * Editable AG Grid controller for the organizer-facing manual-entry ("typing_or_copying") voter
 * list (C3 Phase B / Wave 2). Extends AgGridBaseController (shared toolkit — untouched), mirroring
 * Admin::Elections::AiVoterListGridComponent's own bespoke controller pattern rather than the
 * generic CSRM `ag_grid_component_controller.ts` (which has no editing surface and is shared,
 * widely-reused toolkit infra — see VoterList::EditorGridComponent's class comment for the full
 * "construct AgGridComponent directly" fork rationale).
 *
 * Client-side row model (D1 — NOT SSRM, manual entry is capped at 10,000 rows). Adds on top of the
 * AI grid's single-cell PATCH pattern:
 *   - Range selection + fill handle + clipboard paste (cellSelection, undoRedoCellEditing)
 *   - The change queue/serialization/transport (debounced coalescing, single-cell PATCH vs.
 *     `batch_update`, Hiram HARD-1 per-node serialization, HARD-2 positional response alignment)
 *     now lives in `ag_grid_shared/grid_edit_queue.ts` (`GridEditQueue`, Hiram SOFT-2, Onda 0) — row
 *     model-agnostic, so the future SSRM editor grid reuses the same machinery. This controller
 *     only supplies urls/callbacks (`this.editQueue`) and the row-model-specific bits below.
 *   - Blank entry rows are CLIENT-ONLY, added via `applyTransaction` — no server round-trip. A row
 *     has no `id` until its first cell edit, which routes it through batch_update with `id: null`.
 *     `topUpBlankRows` keeps them available per election mode (5 total for a test election; 10 to
 *     start then 5 trailing blanks for a live one), and a paste creates whatever extra rows it
 *     needs up to the mode's ceiling (`processClipboard`).
 *   - Delete is blank-to-delete only (no explicit affordance) — VoterInformationLine's own
 *     `after_save :destroy, if: :empty_row?` (server-side) removes a row that's edited back to
 *     fully blank; the client then drops any row whose id is missing from a successful response.
 *   - Per-cell error highlighting via the SAME shared `applyErrorFieldCellClassRules` hook
 *     ValidationGridColumnDefs/ag_grid_ssrm_component_controller.ts already use.
 *
 * Wave 3 additions (hardening/parity with elections.voter_list.coffee's Handsontable behavior):
 *   - Dynamic column rebuild: listens for the native `ajax:complete` event (real, bubbling
 *     CustomEvent dispatched by Rails UJS — see `Rails.fire`) on `form.voter_list_election_form`
 *     (the shared voter-settings form) and re-fetches colDefs from
 *     VoterInformationLinesController#column_defs, then `setGridOption('columnDefs', ...)` —
 *     row data is left untouched. Deliberately NOT bound to the legacy `voterSettingsUpdated`
 *     jQuery custom event: that fires client-side BEFORE the settings form's own AJAX PATCH
 *     resolves (see `updateListSettings`/`initElectionFormSubmit` in the coffee controller), which
 *     would race `election.required_columns` (server-computed, via VoterList::GetColumns) against
 *     a not-yet-persisted settings write. `ajax:complete` on the form is the point at which the
 *     server is guaranteed to reflect the new settings.
 *   - `readOnlyValue` (mirrors `_voter_list_js.html.erb:18`'s `!election.created? &&
 *     !election.queued_to_run?`) and an in-flight `validating` flag both feed into
 *     `buildColumnDefs`, which ANDs the per-column server `editable` with "is the grid editable at
 *     all right now" — closing cell editing during either state without touching the per-column
 *     `editable_for` business rule (API input method, Errors column) baked into each colDef.
 *   - Weight format gate (parity with the coffee controller's `NUMERIC_DECIMAL_REGEX`/
 *     `invalidRows`): tracks cells in the `weight` column that fail the same regex and surfaces the
 *     same wording HOT used. It no longer disables a button — this grid renders none; the page's
 *     "Validate list" owns validation, and the server rejects a bad weight regardless.
 *   - Stale-highlight cue: after any successful single-cell/batch mutation, shows the same "The
 *     voter list has changed. Please validate again." message `update_via_legacy_js` already uses
 *     server-side, since canonical error highlighting is only ever refreshed by a real validate.
 */

import {
  createGrid,
  GridApi,
  GridOptions,
  ColDef,
  IRowNode,
  CellValueChangedEvent,
  ProcessDataFromClipboardParams,
  GetContextMenuItemsParams,
  DefaultMenuItem,
  MenuItemDef,
} from "ag-grid-enterprise";
import { AgGridBaseController } from "../../application/toolkit/ag_grid_base_component/ag_grid_base_controller";
import { ebEditableTheme } from "../../application/toolkit/ag_grid_shared/eb_theme";
import { applyErrorFieldCellClassRules } from "../../application/toolkit/ag_grid_shared/error_field_cell_class_rules";
import { GridEditQueue, csrfToken, postJson, extractErrorMessage } from "../../application/toolkit/ag_grid_shared/grid_edit_queue";
import { showConfirmModal } from "../../application/toolkit/ag_grid_shared/confirm_modal";

// JSON request with an optional body — the shared module's `postJson` deliberately sends none, and
// the custom-column endpoints are the only callers that need one (POST) or need DELETE. Reads the
// body as text and parses defensively (same reason as PostSaveGridComponent's local `deleteJson`):
// CustomColumnsController answers a denied organizer with `head :forbidden` — empty, non-JSON —
// which `response.json()` would reject on.
function sendJson(
  url: string,
  method: string,
  body?: unknown
): Promise<{ ok: boolean; status: number; json: Record<string, unknown> }> {
  return fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    credentials: "same-origin",
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  }).then((response) =>
    response.text().then((text) => {
      let json: Record<string, unknown> = {};

      if (text) {
        try {
          const parsed: unknown = JSON.parse(text);
          if (parsed !== null && typeof parsed === "object") json = parsed as Record<string, unknown>;
        } catch {
          // Non-JSON body — the caller falls back to a status-derived message rather than throwing.
        }
      }

      return { ok: response.ok, status: response.status, json };
    })
  );
}

// Parity with elections.voter_list.coffee's NUMERIC_DECIMAL_REGEX weight-format gate.
const NUMERIC_DECIMAL_REGEX = /^[0-9]\d*(\.\d+)?$/;
const WEIGHT_FIELD = "weight";
const ERRORS_FIELD = "errors";

// Hover/focus "you can edit this" affordance marker (scoped SCSS: editor_grid_component.scss).
// Applied client-side in `buildColumnDefs` ONLY — the effective per-cell editable state does not
// exist server-side (EditorGridColumnDefs only knows each column's static business-rule
// `editable`; the grid-wide readOnly/validating gate that ANDs with it is a client-only concern,
// see `buildColumnDefs`/`isEditable`), so this is the single place that can correctly compute it.
const EDITABLE_CELL_CLASS = "voter-list-editor-grid-cell--editable";

// Blank entry rows (see `topUpBlankRows`), mirroring the legacy Handsontable's startRows/
// minSpareRows for this same table (elections.voter_list.coffee:485-491). Never persisted until the
// organizer actually edits a cell (queueChange only fires on real edits; applyTransaction's `add`
// does not trigger it).
const LIVE_INITIAL_BLANK_ROWS = 10;
const TRAILING_BLANK_ROWS = 5;

// Rows per page. Caps how much vertical space the grid can take: with `domLayout: "autoHeight"`,
// the mount is only ever as tall as the current page.
const PAGE_SIZE = 20;

// Client-side mirror of VoterList::CustomColumns::Create's mask and COLUMN_LIMIT. An affordance
// only — the server re-checks all of it, and owns `reserved` (which the client cannot compute).
const CUSTOM_COLUMN_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]*$/;
const CUSTOM_COLUMN_LIMIT = 15;

// Export never appears on this grid's context menu, for the same reason PostSaveGrid strips it:
// AG Grid's stock menu ships an Export submenu, and both voter grids carry the same export-OFF
// contract (Hiram HARD — PII voter data). Cut and paste deliberately STAY on the cell menu, unlike
// the post-save grid: this one is editable and is a real paste target (processDataFromClipboard).
const SUPPRESSED_DEFAULT_MENU_ITEMS: DefaultMenuItem[] = ["export"];

// Nothing on a row NUMBER is editable, so these default items only ever render disabled there —
// hidden instead (product ask, Vinicius 2026-08-18). Copy stays: it acts on the row selection.
const GUTTER_SUPPRESSED_MENU_ITEMS: DefaultMenuItem[] = ["cut", "paste"];

// One paste, as much as is needed to put the grid back: the cells it overwrote on rows that
// already existed, and the rows `growToFit` had to create to fit it.
type PasteUndo = {
  overwritten: Array<{ node: IRowNode; field: string; value: unknown }>;
  added: IRowNode[];
};

interface GroupDelta {
  added: [number, string][];
  removed: [number, string][];
  question_groups: [number, number[]][];
}

declare global {
  interface Window {
    EB?: {
      MultipleSelect?: {
        AddGroups: (questionsWithGroups: [number, number[]][], groups: [number, string][]) => void;
        RemoveGroups: (questionIds: number[], groups: [number, string][]) => void;
      };
    };
  }
}

export default class extends AgGridBaseController {
  static targets = [
    ...AgGridBaseController.targets,
    "grid",
    "validateStatus",
    "weightWarning",
    "newColumnName",
    "addColumnButton",
    "addColumnStatus",
    "declaredColumnChips",
  ];

  static values = {
    ...AgGridBaseController.values,
    rowData: Array,
    updateUrlTemplate: String,
    batchUpdateUrl: String,
    batchDestroyUrl: String,
    validateUrl: String,
    columnDefsUrl: String,
    customColumnsUrl: String,
    declaredColumns: Array,
    maxRowsForTestBallot: Number,
    maxRows: Number,
    readOnly: Boolean,
  };

  declare readonly rowDataValue: Record<string, unknown>[];
  declare readonly updateUrlTemplateValue: string;
  declare readonly batchUpdateUrlValue: string;
  declare readonly batchDestroyUrlValue: string;
  declare readonly hasBatchDestroyUrlValue: boolean;
  declare readonly validateUrlValue: string;
  declare readonly columnDefsUrlValue: string;
  declare readonly customColumnsUrlValue: string;
  declare readonly declaredColumnsValue: string[];
  declare readonly maxRowsForTestBallotValue: number;
  declare readonly maxRowsValue: number;
  declare readonly readOnlyValue: boolean;

  declare readonly validateStatusTarget: HTMLElement;
  declare readonly hasValidateStatusTarget: boolean;
  declare readonly weightWarningTarget: HTMLElement;
  declare readonly hasWeightWarningTarget: boolean;
  declare readonly newColumnNameTarget: HTMLInputElement;
  declare readonly hasNewColumnNameTarget: boolean;
  declare readonly addColumnButtonTarget: HTMLButtonElement;
  declare readonly hasAddColumnButtonTarget: boolean;
  declare readonly addColumnStatusTarget: HTMLElement;
  declare readonly hasAddColumnStatusTarget: boolean;
  declare readonly declaredColumnChipsTarget: HTMLElement;
  declare readonly hasDeclaredColumnChipsTarget: boolean;

  private topUpScheduled = false;

  // Change queue/serialization/transport — see ag_grid_shared/grid_edit_queue.ts. Constructed in
  // `connect()` once urls are available.
  private editQueue!: GridEditQueue;

  // The base colDefs currently in effect (server-provided metadata: headerName, filter,
  // errorFieldKeys, and each column's OWN business-rule `editable`) — separate from the
  // grid-wide editable GATE (`readOnlyValue`/`validating`, see `buildColumnDefs`) so a settings
  // refresh (`refreshColumnDefs`) and a readOnly/validating transition never stomp each other.
  private currentColumnDefs: ColDef[] = [];
  // True for the duration of an in-flight `validate()` POST — mirrors the coffee controller's
  // `updateSettings({readOnly: true})` lock while a validate request is outstanding.
  private validating = false;
  // Client-side mirror of the coffee controller's `invalidRows` — keyed by node.id, valued with the
  // 1-based display row number (for the warning message), for cells in the `weight` column that
  // fail NUMERIC_DECIMAL_REGEX.
  private invalidWeightNodes: Map<string, number> = new Map();

  // Requirement 1 — the Errors column is hidden by default (server default: `hide: true`, see
  // EditorGridColumnDefs#errors_column) and only shown once there IS something to show. Folded
  // into `buildColumnDefs` (rather than a one-shot `setColumnsVisible`) so it survives every colDef
  // rebuild — `refreshColumnDefs` re-fetches server colDefs (always `hide: true`) and re-applies
  // them wholesale on every voter-settings change.
  private showErrorsColumn = false;

  // Declared custom columns, verbatim casing — seeded from the server and replaced wholesale by
  // each #create response (which returns the full array, so a concurrent add lands here too).
  private declaredColumns: string[] = [];
  // Captured on first use so the busy relabel can be undone without hardcoding the copy.
  private addColumnLabel?: string;

  private settingsChangeHandler: ((event: Event) => void) | null = null;
  private undoKeyHandler: ((event: KeyboardEvent) => void) | null = null;
  // `processDataFromClipboard` applies a paste itself and returns [], so AG Grid never sees it and
  // its own undo stack stays empty — nothing to press Ctrl+Z against (QA round 4 item 5). These
  // entries are what this grid undoes instead.
  private pasteUndoStack: PasteUndo[] = [];

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  connect() {
    super.connect(); // license key + outside-click handler

    if (!this.hasGridTarget) return;

    this.currentColumnDefs = this.columnDefsValue;
    this.declaredColumns = Array.isArray(this.declaredColumnsValue) ? [...this.declaredColumnsValue] : [];
    this.renderDeclaredColumnChips();
    // Requirement 1 — initial visibility: show the Errors column immediately if the rows we're
    // about to render already carry errors (e.g. a page reload right after a failed validate),
    // rather than making the organizer re-run Validate just to see errors that are already there.
    this.showErrorsColumn = this.anyRowHasErrors(this.rowDataValue);

    this.editQueue = new GridEditQueue(
      this.updateUrlTemplateValue,
      this.batchUpdateUrlValue,
      {
        onPersisted: (message, payload) => {
          this.clearStaleValidationBanner();
          this.syncGroupSelectors(payload);
          this.showValidateStatusMessage(message, true);
        },
        onRemoveNode: (node) => this.removeNode(node),
      },
      "[EditorGridComponent]"
    );

    const options: GridOptions = {
      // Editable-grid theme (same one AgGridEditableComponent/Growable/provision use): 32px header
      // instead of ebTheme's 56px, tighter 30px rows, and the baby-blue header that matches the
      // legacy Handsontable this grid replaces.
      theme: ebEditableTheme,
      columnDefs: this.buildColumnDefs(),
      rowData: this.rowDataValue,
      // Paginate at 20 rows (house default — see elections/index.html.erb's root grid) so a long
      // list costs a fixed slice of vertical space instead of a tall scrolling panel. `autoHeight`
      // is safe precisely BECAUSE of the pagination: only the current page's rows are ever in the
      // DOM, so the mount grows to at most 20 rows and shrinks to fit a short list — no pixel
      // arithmetic, and no empty band under the last row.
      pagination: true,
      paginationPageSize: PAGE_SIZE,
      paginationPageSizeSelector: [20, 50, 100, 200],
      domLayout: "autoHeight",
      // Object form only to narrow the gutter's own context menu — `colDef.contextMenuItems` wins
      // over the grid default (same mechanism PostSaveGrid documents on its rowNumbers).
      rowNumbers: {
        contextMenuItems: (params: GetContextMenuItemsParams) =>
          this.buildContextMenuItems(
            (params.defaultItems ?? []).filter((item) => !GUTTER_SUPPRESSED_MENU_ITEMS.includes(item)),
            params
          ),
      },
      getContextMenuItems: (params: GetContextMenuItemsParams) =>
        this.buildContextMenuItems(params.defaultItems ?? [], params),
      cellSelection: { handle: { mode: "fill" } },
      undoRedoCellEditing: true,
      undoRedoCellEditingLimit: 20,
      stopEditingWhenCellsLoseFocus: true,
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
      },
      processDataFromClipboard: (params: ProcessDataFromClipboardParams) => this.processClipboard(params),
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.queueChange(event);
      },
      // Picking a different page size from the paginator changes what "a whole number of pages"
      // means, so re-round the row count. Also fires on plain page navigation, where the top-up is a
      // no-op. Coalesced, so it cannot recurse through its own row insert.
      onPaginationChanged: () => this.scheduleBlankRowTopUp(),
    };

    this.gridApi = createGrid(this.gridTarget, options);
    this.scanInitialWeights();
    this.topUpBlankRows();

    this.settingsChangeHandler = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.("form.voter_list_election_form")) this.refreshColumnDefs();
    };
    document.addEventListener("ajax:complete", this.settingsChangeHandler);

    this.undoKeyHandler = (event: KeyboardEvent) => this.interceptUndo(event);
    this.gridTarget.addEventListener("keydown", this.undoKeyHandler, true);
  }

  disconnect() {
    if (this.settingsChangeHandler) {
      document.removeEventListener("ajax:complete", this.settingsChangeHandler);
      this.settingsChangeHandler = null;
    }
    if (this.undoKeyHandler && this.hasGridTarget) {
      this.gridTarget.removeEventListener("keydown", this.undoKeyHandler, true);
      this.undoKeyHandler = null;
    }
    super.disconnect();
  }

  // Required stubs from the abstract base (no basic/advanced view toggle in this grid)
  switchToBasic(): void { /* not used */ }
  switchToAdvanced(): void { /* not used */ }

  // ── Public actions ─────────────────────────────────────────────────────────

  // Kept as a public action even though this grid no longer renders its own Validate button — the
  // page's "Validate list" button owns validation now (see the erb template). This is the in-place
  // path (no re-render, keeps scroll/focus, jumps to the first error) if we ever wire that button
  // straight into the grid instead of going through validate_voter_list.js.erb.
  validate(): void {
    if (!this.validateUrlValue || !this.gridApi || this.readOnlyValue) return;

    // Parity with the coffee controller's `updateSettings({readOnly: true})` lock in
    // `initVoterListClearAndValidateButtons` — the grid is non-editable for the duration of the
    // validate request.
    this.validating = true;
    this.applyGridEditableState();

    postJson(this.validateUrlValue)
      .then(({ ok, json }) => {
        if (!ok) throw new Error(`Validate failed`);
        const rows = Array.isArray(json.rows) ? json.rows : [];
        this.gridApi?.setGridOption("rowData", rows);
        // Fresh nodes: anything the paste stack still points at no longer exists.
        this.pasteUndoStack = [];
        // Hiram S1 — an empty validate result (e.g. a freshly-seeded, still-blank grid) wipes the
        // rowData swap above down to zero rows; re-seed immediately so the 5 blank entry rows come
        // right back rather than leaving the organizer looking at a permanently empty grid.
        this.topUpBlankRows();
        // A full rowData swap creates fresh row nodes — any previously-tracked weight-invalid
        // node.id keys no longer correspond to a real row.
        this.invalidWeightNodes.clear();
        this.scanInitialWeights();
        // Requirement 1 / Hiram A3 — show the Errors column only when a returned row actually has
        // per-row content for it (`anyRowHasErrors`, computed on the FRESH `rows`), not merely
        // `!json.valid` — a list-level-only failure or an empty validate result has no per-row
        // error_fields to display, so the column should stay hidden even though `valid` is false.
        // Re-apply colDefs so the fresh hide state renders immediately (must run AFTER the rowData
        // swap above, so `jumpToFirstError`/cellClassRules below see the same fresh error data).
        this.showErrorsColumn = this.anyRowHasErrors(rows);
        this.applyGridEditableState();
        this.showValidateStatus(json.valid as boolean, json.errors_count as number);
        this.jumpToFirstError();
      })
      .catch((error) => {
        console.error("[EditorGridComponent] validate error:", error);
        this.showValidateStatusMessage("Validation request failed", true);
      })
      .finally(() => {
        this.validating = false;
        this.applyGridEditableState();
      });
  }

  // Declares a new custom voter-list column (VoterList::CustomColumnsController#create) and pulls
  // it into the grid via the existing colDef refresh. Pre-save editor only — after validation a
  // declared column is read-only, exactly like an unmapped CSV header.
  addColumn(event?: Event): void {
    event?.preventDefault();
    if (!this.hasNewColumnNameTarget || !this.customColumnsUrlValue) return;

    const name = this.newColumnNameTarget.value.trim();
    const clientError = this.addColumnClientError(name);
    if (clientError) {
      this.showAddColumnStatus(clientError, true);
      return;
    }

    this.setAddColumnBusy(true);
    this.showAddColumnStatus(`Adding "${name}"\u2026`, false, true);
    // Dispatch anything sitting in the edit queue before the colDef swap below. GridEditQueue
    // exposes no way to AWAIT the requests it fires, so this only narrows the window.
    this.editQueue?.flush();

    sendJson(this.customColumnsUrlValue, "POST", { name })
      .then(({ ok, status, json }) => {
        if (!ok) {
          this.showAddColumnStatus(this.addColumnErrorMessage(status, json), true);
          return;
        }

        const added = typeof json.name === "string" ? json.name : name;
        this.declaredColumns = Array.isArray(json.custom_voter_columns)
          ? (json.custom_voter_columns as string[])
          : [...this.declaredColumns, added];
        this.newColumnNameTarget.value = "";
        this.renderDeclaredColumnChips();

        // The column is already saved at this point, so a failed refresh would otherwise leave it
        // invisible with nothing but a console error — say so instead.
        return this.refreshColumnDefs().then((refreshed) => {
          this.showAddColumnStatus(
            refreshed
              ? `Added "${added}".`
              : `Added "${added}", but the grid could not be refreshed — reload the page to see it.`,
            !refreshed
          );
        });
      })
      .catch((error) => {
        console.error("[EditorGridComponent] add column error:", error);
        this.showAddColumnStatus("Could not add the column. Please try again.", true);
      })
      .finally(() => this.setAddColumnBusy(false));
  }

  private addColumnClientError(name: string): string | null {
    if (name === "") return "Enter a name for the new column.";
    if (!CUSTOM_COLUMN_NAME_PATTERN.test(name)) {
      return "Use only letters, numbers and underscores (no spaces), beginning with a letter.";
    }
    if (this.declaredColumns.length >= CUSTOM_COLUMN_LIMIT) {
      return `You can add up to ${CUSTOM_COLUMN_LIMIT} custom columns.`;
    }
    if (this.declaredColumns.some((column) => column.toLowerCase() === name.toLowerCase())) {
      return `"${name}" is already one of your custom columns.`;
    }
    return null;
  }

  // The server's `errors` string is written for the organizer, so it renders verbatim. A 403 has no
  // body at all (`head :forbidden`).
  private addColumnErrorMessage(status: number, json: Record<string, unknown>): string {
    if (typeof json.errors === "string" && json.errors !== "") return json.errors;
    if (status === 403) return "You do not have permission to change this voter list.";
    return "Could not add the column. Please try again.";
  }

  // One chip per declared column, rebuilt from `declaredColumns` after every add and remove. Built
  // node by node rather than through innerHTML: the names arrive as JSON from the server and belong
  // in the DOM as text.
  private renderDeclaredColumnChips(): void {
    if (!this.hasDeclaredColumnChipsTarget) return;

    const container = this.declaredColumnChipsTarget;
    container.textContent = "";
    container.classList.toggle("hide", this.declaredColumns.length === 0);

    this.declaredColumns.forEach((name) => {
      const chip = document.createElement("span");
      chip.className = "voter-list-editor-grid-add-column__chip";

      const label = document.createElement("span");
      label.textContent = name;
      chip.appendChild(label);

      const remove = document.createElement("button");
      remove.type = "button";
      // Same two classes the Add button carries — without them the coffee bootstrap submits the
      // Voter Options form on interaction with any control on this page.
      remove.className =
        "voter-list-editor-grid-add-column__chip-remove skip_auto_submit skip_update_settings";
      remove.setAttribute("aria-label", `Remove column ${name}`);
      remove.textContent = "×";
      remove.addEventListener("click", () => this.removeColumn(name));
      chip.appendChild(remove);

      container.appendChild(chip);
    });
  }

  // Removing a column discards whatever was typed into it, so this always confirms — unlike the row
  // delete, which product deliberately left unconfirmed.
  private removeColumn(name: string): void {
    if (!this.customColumnsUrlValue) return;

    void showConfirmModal({
      title: "Remove column",
      danger: `Removing "${name}" also deletes anything already typed into it.`,
      message: "This cannot be undone.",
      confirmLabel: "Remove column",
    }).then((confirmed) => {
      if (!confirmed) return undefined;

      this.setRemoveColumnBusy(true);
      this.showAddColumnStatus(`Removing "${name}"…`, false, true);
      this.editQueue?.flush();

      return sendJson(`${this.customColumnsUrlValue}/${encodeURIComponent(name)}`, "DELETE")
        .then(({ ok, status, json }) => {
          if (!ok) {
            this.showAddColumnStatus(this.removeColumnErrorMessage(status, json), true);
            return undefined;
          }

          this.declaredColumns = Array.isArray(json.custom_voter_columns)
            ? (json.custom_voter_columns as string[])
            : this.declaredColumns.filter((column) => column.toLowerCase() !== name.toLowerCase());
          this.renderDeclaredColumnChips();

          // The column is already gone server-side, so a failed refresh would otherwise leave it on
          // screen with nothing but a console error.
          return this.refreshColumnDefs().then((refreshed) => {
            this.showAddColumnStatus(
              refreshed
                ? `Removed "${name}".`
                : `Removed "${name}", but the grid could not be refreshed — reload the page.`,
              !refreshed
            );
          });
        })
        .catch((error) => {
          console.error("[EditorGridComponent] remove column error:", error);
          this.showAddColumnStatus("Could not remove the column. Please try again.", true);
        })
        .finally(() => this.setRemoveColumnBusy(false));
    });
  }

  private removeColumnErrorMessage(status: number, json: Record<string, unknown>): string {
    if (typeof json.errors === "string" && json.errors !== "") return json.errors;
    if (status === 403) return "You do not have permission to change this voter list.";
    return "Could not remove the column. Please try again.";
  }

  private setRemoveColumnBusy(busy: boolean): void {
    if (!this.hasDeclaredColumnChipsTarget) return;

    this.declaredColumnChipsTarget.querySelectorAll<HTMLButtonElement>("button").forEach((button) => {
      button.disabled = busy;
    });
  }

  // Visible loading state: the POST is followed by a colDefs refetch, so this is not instant.
  private setAddColumnBusy(busy: boolean): void {
    if (this.hasNewColumnNameTarget) this.newColumnNameTarget.disabled = busy;
    if (!this.hasAddColumnButtonTarget) return;

    const button = this.addColumnButtonTarget;
    if (busy) {
      this.addColumnLabel ??= button.textContent ?? "Add";
      button.textContent = "Adding\u2026";
    } else if (this.addColumnLabel) {
      button.textContent = this.addColumnLabel;
    }
    button.disabled = busy;
    button.setAttribute("aria-busy", String(busy));
  }

  private showAddColumnStatus(message: string, isError: boolean, busy = false): void {
    if (!this.hasAddColumnStatusTarget) return;

    const state = message === "" ? "hide" : busy ? "is-busy" : isError ? "inline_error" : "inline_success";
    this.addColumnStatusTarget.textContent = message;
    this.addColumnStatusTarget.className = `voter-list-editor-grid-add-column__status ${state}`;
  }

  // ── Change queue (single-cell PATCH vs. batch_update — machinery in GridEditQueue) ──────────

  // Row-model-specific side effects (weight tracking, blank-row top-up) plus the suppress-guard
  // (rollback re-triggering this same handler) live here; persistence itself is `this.editQueue`'s.
  private queueChange(event: CellValueChangedEvent): void {
    if (this.editQueue.suppressed) return;

    const field = event.colDef.field;
    if (!field) return;

    if (field === WEIGHT_FIELD) this.trackWeightValidity(event);

    this.editQueue.queueChange(event.node, field, event.newValue, event.oldValue);

    // Keep the trailing blank rows topped up as the list grows. Coalesced onto a microtask: a
    // paste fires one CellValueChanged PER CELL, and topping up walks every row twice, so calling
    // it inline would make a large paste quadratic.
    this.scheduleBlankRowTopUp();
  }

  private scheduleBlankRowTopUp(): void {
    if (this.topUpScheduled) return;
    this.topUpScheduled = true;
    queueMicrotask(() => {
      this.topUpScheduled = false;
      this.topUpBlankRows();
    });
  }

  // ── Right-click delete ───────────────────────────────────────────────────
  //
  // Ported from PostSaveGridComponent, which shares the `batch_destroy` endpoint. Two deliberate
  // differences: this grid has no `rowSelection` (only `cellSelection`, whose fill handle owns the
  // drag gesture), so the item always acts on the CLICKED row alone; and removal is a client-side
  // `applyTransaction` through `removeNode` rather than an SSRM purge.
  private buildContextMenuItems(
    defaults: (DefaultMenuItem | MenuItemDef)[],
    params: GetContextMenuItemsParams
  ): (DefaultMenuItem | MenuItemDef)[] {
    // Applied to BOTH entry points (cell menu and gutter), so neither can leak an Export submenu.
    const allowed = defaults.filter(
      (item) => typeof item !== "string" || !SUPPRESSED_DEFAULT_MENU_ITEMS.includes(item)
    );

    if (this.readOnlyValue || !this.hasBatchDestroyUrlValue) return allowed;

    const node = params.node;
    const id = Number((node?.data as { id?: unknown } | undefined)?.id);
    // A seed row that has never been saved has no id yet — nothing to ask the server to delete.
    if (!node || !Number.isInteger(id) || id <= 0) return allowed;

    const deleteItem: MenuItemDef = {
      name: "Delete voter",
      icon: '<i class="fas fa-trash" aria-hidden="true"></i>',
      action: () => {
        void this.deleteRow(node, id);
      },
    };

    return allowed.length > 0 ? [...allowed, "separator", deleteItem] : [deleteItem];
  }

  // Same dispatch-before-delete ordering PostSaveGrid documents: a queued edit for this row must go
  // out before the DELETE, so it can never settle against a row the server has already removed.
  private async deleteRow(node: IRowNode, id: number): Promise<void> {
    this.editQueue?.flush();

    let ok = false;
    let status = 0;
    let json: Record<string, unknown> = {};

    try {
      ({ ok, status, json } = await sendJson(this.batchDestroyUrlValue, "DELETE", { ids: [id] }));
    } catch {
      this.showValidateStatusMessage("Could not reach the server. No voters were removed.", true);
      return;
    }

    if (!ok) {
      this.showValidateStatusMessage(this.deleteErrorMessage(status, json), true);
      return;
    }

    this.removeNode(node);
    this.clearStaleValidationBanner();
    this.showValidateStatusMessage("1 voter removed. The voter list has changed. Please validate again.", false);
  }

  // BatchDestroy's own failures carry an `errors` payload and are quoted verbatim. A rejection from
  // ABOVE it has no payload to quote (`authorize_election!` answers an empty `head :forbidden`), so
  // those are worded from the status code instead.
  private deleteErrorMessage(status: number, json: Record<string, unknown>): string {
    if (json.errors !== undefined) return extractErrorMessage(json);
    if (status === 403) return "You do not have permission to remove voters from this list.";

    return "Could not remove the voter. Please try again.";
  }

  // Shared by every "the server (or we) removed this row" path — GridEditQueue's null-row
  // reconciliation and failed-new-row cleanup both route here via `onRemoveNode`.
  private removeNode(node: IRowNode): void {
    this.invalidWeightNodes.delete(String(node.id));
    this.updateWeightGate();
    this.gridApi?.applyTransaction({ remove: [node.data] });
    // Removing a row can take the count off a whole-page boundary (e.g. 40 -> 39), which would make
    // the last page shorter than the rest — top back up to a full page.
    this.scheduleBlankRowTopUp();
  }

  // ── Column defs / editable-state gate (dynamic rebuild, readOnly, validating) ────────────────

  // ANDs each colDef's OWN server-computed `editable` (business rule — API input method, the
  // Errors column) with whether the GRID AS A WHOLE is editable right now, then re-applies the
  // shared error-highlighting cellClassRules hook (which itself derives from `errorFieldKeys`, not
  // `editable`, so it survives either input untouched).
  private buildColumnDefs(): ColDef[] {
    const gridEditable = this.isEditable();
    const withGateApplied = this.currentColumnDefs.map((col) => {
      const editable = gridEditable && Boolean(col.editable);
      return {
        ...col,
        editable,
        // Visual affordance (support ticket reduction) — mark cells that are EFFECTIVELY editable
        // right now, i.e. after ANDing the column's own business-rule `editable` with the grid-wide
        // gate, so a statically-editable column correctly loses the affordance the instant the grid
        // goes read-only/validating, and regains it the instant it doesn't — same rebuild trigger as
        // `editable` itself, right above. The Errors column (`editable_for` never includes it) and
        // every column during readOnly/validating never get the marker. Composed defensively with
        // any pre-existing `cellClass` (EditorGridColumnDefs never sets one today, but this must not
        // silently clobber one if that ever changes).
        cellClass: editable ? this.withEditableCellClass(col.cellClass) : col.cellClass,
        // Requirement 1 — override the server's `hide: true` default (EditorGridColumnDefs#
        // errors_column) with the client's current `showErrorsColumn` state. Folded in here (rather
        // than a one-shot `setColumnsVisible`) so visibility survives every colDef rebuild —
        // `refreshColumnDefs` re-fetches server colDefs (always `hide: true`) and re-applies them
        // wholesale on every voter-settings change.
        ...(col.field === ERRORS_FIELD ? { hide: !this.showErrorsColumn } : {}),
      };
    });
    // `applyErrorFieldCellClassRules` only ever sets `cellClassRules` (from the plain-JSON
    // `errorFieldKeys` marker) and spreads every other key through untouched — confirmed against
    // its source (ag_grid_shared/error_field_cell_class_rules.ts): `{...rest, cellClassRules: {...
    // ...(col.cellClassRules ?? {})}}`, where `rest` is `col` minus `errorFieldKeys`. `cellClass`
    // and `cellClassRules` are independent ColDef props, so the marker set above survives this call
    // untouched for every column, whether or not it also carries `errorFieldKeys`.
    return applyErrorFieldCellClassRules(withGateApplied);
  }

  // Defensive merge for `ColDef["cellClass"]`'s full union (`string | string[] |
  // CellClassFunc<...>`). The server sends plain JSON (EditorGridColumnDefs), so a function value
  // never actually occurs at runtime; if `cellClass` is ever populated server-side as a function,
  // it is dropped here rather than merged (a function can't be composed with a static marker
  // string without invoking it against cell params we don't have at colDef-build time) — flagged
  // as a known limitation, not expected to occur given the current column-def source.
  private withEditableCellClass(cellClass: ColDef["cellClass"]): string[] {
    const existing = Array.isArray(cellClass) ? cellClass : typeof cellClass === "string" ? [cellClass] : [];
    return [...existing, EDITABLE_CELL_CLASS];
  }

  private isEditable(): boolean {
    return !this.readOnlyValue && !this.validating;
  }

  private applyGridEditableState(): void {
    this.gridApi?.setGridOption("columnDefs", this.buildColumnDefs());
  }

  // Re-fetches colDefs from VoterInformationLinesController#column_defs (server-computed
  // `election.required_columns`) and applies ONLY the columnDefs grid option — row data is left
  // completely untouched, per the brief. See the class doc for why this listens to the settings
  // FORM's `ajax:complete` rather than the legacy `voterSettingsUpdated` custom event.
  //
  // Resolves false when the refresh did not land (empty payload, network/parse error) — the
  // settings path ignores that, but `addColumn` has to report it: by then the column IS saved.
  private refreshColumnDefs(): Promise<boolean> {
    if (!this.gridApi || !this.columnDefsUrlValue) return Promise.resolve(false);

    return fetch(this.columnDefsUrlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((response) => response.json())
      .then((json) => {
        const columnDefs = Array.isArray(json.column_defs) ? (json.column_defs as ColDef[]) : [];
        if (columnDefs.length === 0) return false;

        this.currentColumnDefs = columnDefs;
        this.applyGridEditableState();
        return true;
      })
      .catch((error) => {
        console.error("[EditorGridComponent] column_defs refresh error:", error);
        return false;
      });
  }

  // ── Weight format gate (parity with elections.voter_list.coffee's afterValidate) ─────────────

  private trackWeightValidity(event: CellValueChangedEvent): void {
    const key = String(event.node.id);
    const value = event.newValue;
    const valid = !value || NUMERIC_DECIMAL_REGEX.test(String(value));

    if (valid) {
      this.invalidWeightNodes.delete(key);
    } else {
      this.invalidWeightNodes.set(key, (event.node.rowIndex ?? 0) + 1);
    }

    this.updateWeightGate();
  }

  // Scans the grid's CURRENT rows for weight-format violations — covers values that arrived
  // already-invalid (initial page load, or a fresh rowData swap after `validate()`), not just ones
  // introduced by a live edit (`trackWeightValidity` alone would miss those).
  private scanInitialWeights(): void {
    if (!this.gridApi) return;

    this.gridApi.forEachNode((node, index) => {
      const value = (node.data as Record<string, unknown> | undefined)?.[WEIGHT_FIELD];
      const valid = value === null || value === undefined || value === "" || NUMERIC_DECIMAL_REGEX.test(String(value));
      if (!valid) this.invalidWeightNodes.set(String(node.id), index + 1);
    });

    this.updateWeightGate();
  }

  private updateWeightGate(): void {
    const hasInvalidWeights = this.invalidWeightNodes.size > 0;
    // The warning message is now the whole gate: with this grid's own Validate button gone, there is
    // no button here to disable, and the page's "Validate list" button is not ours to reach into.
    // An invalid weight still surfaces below the grid AND still fails server-side validation.
    if (!this.hasWeightWarningTarget) return;

    if (!hasInvalidWeights) {
      this.weightWarningTarget.textContent = "";
      this.weightWarningTarget.classList.add("hide");
      return;
    }

    const rows = Array.from(this.invalidWeightNodes.values()).sort((a, b) => a - b);
    const label = rows.length === 1 ? "row" : "rows";
    this.weightWarningTarget.textContent =
      `Fractional weights must be formatted with decimals. Invalid values found on ${label} ${rows.join(", ")}.`;
    this.weightWarningTarget.classList.remove("hide");
  }

  // ── Clipboard / row helpers ──────────────────────────────────────────────────

  // Makes a paste land in full instead of being silently clipped.
  //
  // COLUMNS: trimmed to the data columns this grid actually has (Errors excluded — never a paste
  // destination), since AG Grid otherwise keeps applying values past the last real column.
  //
  // ROWS: AG Grid only pastes into rows that already exist and DROPS the rest without a word, so
  // pasting a 300-voter list into a 10-row grid used to lose 290 voters. We create the missing rows
  // here first (the documented use for this callback), which is also what the legacy Handsontable
  // did via `minSpareRows`. Trailing blank lines that spreadsheets append to a copy are dropped so
  // they don't inflate the row count.
  //
  // CEILING: a test election still stops at its ballot cap, and a live one at the manual-entry row
  // limit — the paste is truncated to what fits and the organizer is told, rather than the browser
  // being asked to render an unbounded list or the server rejecting the batch later.
  // Applies the paste ourselves and CANCELS AG Grid's own (returning [] — an empty payload leaves it
  // nothing to write).
  //
  // Why not let it do the work: AG Grid pastes only into the CURRENT PAGE and silently drops the
  // rest, so with 20-row pagination a 300-row list lost everything past row 20 — the rows we create
  // below exist in the model, just on later pages the native paste never reaches. Writing the cells
  // ourselves is page-agnostic. Each `setDataValue` still fires onCellValueChanged, so persistence,
  // blank-row top-up and error highlighting all run exactly as they do for a typed edit.
  private processClipboard(params: ProcessDataFromClipboardParams): string[][] {
    if (!this.gridApi) return params.data;

    const fields = this.pasteTargetFields();
    if (fields.length === 0) return [];

    const rows = this.trimmedClipboardRows(params.data, fields.length);
    const startRow = this.gridApi.getFocusedCell()?.rowIndex ?? 0;
    const ceiling = this.rowCeiling();
    const capacity = Math.max(0, ceiling - startRow);

    if (rows.length > capacity) {
      this.showValidateStatusMessage(this.pasteTruncatedMessage(rows.length, capacity), true);
      rows.length = capacity;
    }
    if (rows.length === 0) return [];

    const rowCountBeforePaste = this.currentRowCount();
    const overwritten = this.snapshotCells(rows, startRow, fields);

    this.growToFit(startRow + rows.length);
    this.writeClipboardRows(rows, startRow, fields);

    this.pasteUndoStack.push({ overwritten, added: this.nodesFrom(rowCountBeforePaste) });

    return [];
  }

  // The columns a paste lands on: from the focused one rightwards, skipping Errors (never a paste
  // destination) and anything not currently editable.
  private pasteTargetFields(): string[] {
    const editable = this.buildColumnDefs()
      .filter((col) => col.field && col.field !== ERRORS_FIELD && col.editable)
      .map((col) => col.field as string);

    const focusedField = this.gridApi?.getFocusedCell()?.column?.getColId();
    const startIndex = focusedField ? editable.indexOf(focusedField) : 0;

    return startIndex > 0 ? editable.slice(startIndex) : editable;
  }

  private trimmedClipboardRows(data: string[][], columnCount: number): string[][] {
    const rows = data.map((row) => row.slice(0, columnCount));

    // Spreadsheets append a trailing empty line to a copied range.
    while (rows.length > 0 && rows[rows.length - 1].every((cell) => (cell ?? "").trim() === "")) {
      rows.pop();
    }
    return rows;
  }

  private growToFit(rowCount: number): void {
    const missing = rowCount - this.currentRowCount();
    if (missing <= 0) return;

    this.gridApi?.applyTransaction({ add: Array.from({ length: missing }, () => this.blankRow()) });
  }

  private snapshotCells(
    rows: string[][],
    startRow: number,
    fields: string[]
  ): Array<{ node: IRowNode; field: string; value: unknown }> {
    const nodes = this.allNodes();
    const snapshot: Array<{ node: IRowNode; field: string; value: unknown }> = [];

    rows.forEach((row, offset) => {
      const node = nodes[startRow + offset];
      if (!node) return;

      row.forEach((_value, index) => {
        const field = fields[index];
        if (!field) return;

        snapshot.push({ node, field, value: (node.data as Record<string, unknown>)?.[field] ?? null });
      });
    });

    return snapshot;
  }

  private nodesFrom(index: number): IRowNode[] {
    return this.allNodes().slice(index);
  }

  private allNodes(): IRowNode[] {
    const nodes: IRowNode[] = [];
    this.gridApi?.forEachNode((node) => nodes.push(node));
    return nodes;
  }

  // AG Grid keeps owning Ctrl+Z while it has anything of its own to undo, so a cell edited after a
  // paste reverts before the paste does. Only once its stack is empty does the paste come off ours.
  private interceptUndo(event: KeyboardEvent): void {
    if (!(event.ctrlKey || event.metaKey) || event.shiftKey || event.key.toLowerCase() !== "z") return;
    if (this.pasteUndoStack.length === 0) return;
    if ((this.gridApi?.getCurrentUndoSize() ?? 0) > 0) return;

    event.preventDefault();
    event.stopPropagation();
    void this.undoLastPaste();
  }

  private async undoLastPaste(): Promise<void> {
    const entry = this.pasteUndoStack.pop();
    if (!entry) return;

    // Restoring through setDataValue is what persists the reversal: it fires onCellValueChanged,
    // which is the same path a typed edit takes to the server.
    entry.overwritten.forEach(({ node, field, value }) => node.setDataValue(field, value));

    const ids = entry.added
      .map((node) => Number((node.data as { id?: unknown } | undefined)?.id))
      .filter((id) => Number.isInteger(id) && id > 0);

    // The queued restores must settle before the DELETE, so an edit can never land on a row the
    // server has already dropped — the ordering deleteRow documents.
    this.editQueue?.flush();

    if (ids.length > 0 && this.hasBatchDestroyUrlValue) {
      let ok = false;
      let status = 0;
      let json: Record<string, unknown> = {};

      try {
        ({ ok, status, json } = await sendJson(this.batchDestroyUrlValue, "DELETE", { ids }));
      } catch {
        this.showValidateStatusMessage("Could not reach the server. The paste was not undone.", true);
        return;
      }

      if (!ok) {
        this.showValidateStatusMessage(this.deleteErrorMessage(status, json), true);
        return;
      }
    }

    entry.added.forEach((node) => this.removeNode(node));
    this.clearStaleValidationBanner();
    this.showValidateStatusMessage("Paste undone. The voter list has changed. Please validate again.", false);
  }

  private writeClipboardRows(rows: string[][], startRow: number, fields: string[]): void {
    const nodes = this.allNodes();

    rows.forEach((row, offset) => {
      const node = nodes[startRow + offset];
      if (!node) return;

      row.forEach((value, index) => {
        const field = fields[index];
        if (!field) return;

        node.setDataValue(field, value === "" ? null : value);
      });
    });
  }

  // Hard ceiling on how many rows this grid may hold: the ballot cap for a test election, otherwise
  // the manual-entry row limit (`EditorGridComponent::MAX_ROWS`, the same one behind the
  // over-capacity banner). `BatchUpdate` remains the server-side enforcement boundary.
  private rowCeiling(): number {
    if (this.maxRowsForTestBallotValue > 0) return this.maxRowsForTestBallotValue;
    return this.maxRowsValue > 0 ? this.maxRowsValue : Number.MAX_SAFE_INTEGER;
  }

  private pasteTruncatedMessage(pasted: number, kept: number): string {
    if (this.maxRowsForTestBallotValue > 0) {
      return `Pasted ${pasted} rows but this is a test election, which is limited to ` +
        `${this.maxRowsForTestBallotValue} voters — only the first ${kept} were added.`;
    }

    return `Pasted ${pasted} rows but manual entry is limited to ${this.maxRowsValue} voters — ` +
      `only the first ${kept} were added. Import the full list as a CSV file instead.`;
  }

  // NOTE the test-ballot cap needs no client-side guard any more: `topUpBlankRows` never grows a
  // test election's grid past `maxRowsForTestBallotValue`, and paste can only fill rows that
  // already exist (`trimClipboardData`), never create them. `BatchUpdate` remains the enforcement
  // boundary either way.
  private blankRow(): Record<string, unknown> {
    const row: Record<string, unknown> = {};
    this.currentColumnDefs.forEach((col) => {
      if (col.field && col.field !== "errors") row[col.field] = null;
    });
    return row;
  }

  // Requirement 1 — true if ANY row already carries validation errors (row shape from
  // ValidationGridRowSerializer: `error_fields` is an array of error keys, `errors` is the
  // flattened message string).
  private anyRowHasErrors(rows: Record<string, unknown>[]): boolean {
    return rows.some((row) => {
      const errorFields = row.error_fields as unknown[] | undefined;
      const errors = row.errors as string | undefined;
      return (Array.isArray(errorFields) && errorFields.length > 0) || Boolean(errors && errors.length > 0);
    });
  }

  // Keeps blank entry rows available, matching the legacy Handsontable's startRows/minSpareRows
  // behaviour (elections.voter_list.coffee:485-491):
  //
  //   test election  — the grid always shows exactly the cap's worth of rows (5), filled rows
  //                    included, so the organizer sees every slot they are allowed to use and
  //                    never one they aren't.
  //   live election  — starts at 10 blank rows and keeps 5 blank ones trailing the last filled
  //                    row: fill the 6th of 10 and an 11th appears.
  //
  // Never fires for a read-only election. `applyTransaction`'s `add` does not trigger
  // `onCellValueChanged`, so topping up cannot recurse through `queueChange`. Reads the grid's
  // ACTUAL row count (Hiram S1), not the initial `rowDataValue` attribute, so it is safe to re-run
  // after a `validate()` rowData swap as well as at `connect()`.
  private topUpBlankRows(): void {
    if (!this.gridApi || !this.isEditable()) return;

    const total = this.currentRowCount();
    let target: number;

    if (this.maxRowsForTestBallotValue > 0) {
      target = this.maxRowsForTestBallotValue;
    } else if (total === 0) {
      target = LIVE_INITIAL_BLANK_ROWS;
    } else {
      target = total - this.trailingBlankRowCount() + TRAILING_BLANK_ROWS;
    }

    // Once the list is long enough to paginate, round the row count up to a whole number of pages:
    // with `domLayout: "autoHeight"` a short final page would be shorter than the others, so the
    // grid would visibly resize as the organizer moves between pages. Uses the CURRENT page size,
    // so this still holds if they pick another size from the paginator. (The live ceiling,
    // EditorGridComponent::MAX_ROWS = 10,000, is itself a whole number of 20-row pages.)
    const pageSize = this.gridApi.paginationGetPageSize() || PAGE_SIZE;
    if (target > pageSize) target = Math.ceil(target / pageSize) * pageSize;

    // Never grows past the mode's ceiling, and never REMOVES rows: shrinking the grid under an
    // organizer who just blanked a row out would move everything below it.
    const needed = Math.min(target, this.rowCeiling()) - total;
    if (needed <= 0) return;

    this.gridApi.applyTransaction({ add: Array.from({ length: needed }, () => this.blankRow()) });
  }

  // Blank rows trailing the last filled row — what `topUpBlankRows` tops back up to
  // TRAILING_BLANK_ROWS. Counts from the bottom and stops at the first filled row, so blank gaps
  // left in the middle of a list are deliberately not counted.
  private trailingBlankRowCount(): number {
    const nodes: IRowNode[] = [];
    this.gridApi?.forEachNode((node) => nodes.push(node));

    let count = 0;
    for (let index = nodes.length - 1; index >= 0; index -= 1) {
      if (!this.isBlankRowData(nodes[index].data as Record<string, unknown> | undefined)) break;
      count += 1;
    }
    return count;
  }

  private isBlankRowData(data: Record<string, unknown> | undefined): boolean {
    if (!data) return false;

    return this.currentColumnDefs.every((col) => {
      if (!col.field || col.field === "errors") return true;

      const value = data[col.field];
      return value === null || value === undefined || String(value).trim() === "";
    });
  }

  // Hiram S1 — the grid's actual current row count. Deliberately NOT `getDisplayedRowCount()`,
  // which respects active column filters: a user who filtered every row out and then clicked
  // Validate would falsely read 0 displayed rows and get spurious seed rows injected underneath
  // their filter.
  private currentRowCount(): number {
    let count = 0;
    this.gridApi?.forEachNode(() => {
      count += 1;
    });
    return count;
  }

  private jumpToFirstError(): void {
    if (!this.gridApi) return;
    let targetIndex: number | null = null;
    this.gridApi.forEachNode((node, index) => {
      if (targetIndex !== null) return;
      const errorFields = (node.data as Record<string, unknown>)?.error_fields as unknown[] | undefined;
      if (errorFields && errorFields.length > 0) targetIndex = index;
    });
    if (targetIndex !== null) this.gridApi.ensureIndexVisible(targetIndex, "middle");
  }

  // ── Validate status UI ───────────────────────────────────────────────────────

  private showValidateStatus(valid: boolean, errorsCount: number): void {
    if (valid) {
      this.showValidateStatusMessage("Valid — no errors", false);
    } else {
      const count = errorsCount ?? 0;
      this.showValidateStatusMessage(`${count} error${count === 1 ? "" : "s"} found`, true);
    }
  }

  // The page's "Validate list" button owns #voter_list_notices; this grid's JSON round-trip never
  // re-renders it, so a persisted edit left the previous "Everything looks good" banner standing
  // against the stale-list warning this grid had just shown (QA round 3 item 4). Emptied, not
  // removed — the validate response still replaces this node.
  private clearStaleValidationBanner(): void {
    ["voter_list_notices", "secondary_voter_list_notices"].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.innerHTML = "";
    });
  }

  // The "Group settings" selector is page furniture, outside this grid: a Group created by a paste
  // reached the database and stayed invisible there until a reload, because this grid's JSON round
  // trip refreshes nothing but its own rows. Same calls the legacy :js path makes through
  // voter_information_lines/update.js.erb.
  private syncGroupSelectors(payload?: Record<string, unknown>): void {
    const groups = payload?.groups as GroupDelta | undefined;
    const multipleSelect = window.EB?.MultipleSelect;
    if (!groups || !multipleSelect) return;

    if (groups.removed?.length) {
      multipleSelect.RemoveGroups(groups.question_groups.map(([questionId]) => questionId), groups.removed);
    }
    if (groups.added?.length) {
      multipleSelect.AddGroups(groups.question_groups, groups.added);
    }
  }

  private showValidateStatusMessage(message: string, isError: boolean): void {
    if (!this.hasValidateStatusTarget) return;
    this.validateStatusTarget.textContent = message;
    this.validateStatusTarget.className = `voter-list-editor-grid-validate-status ${isError ? "inline_error" : "inline_success"}`;
  }

}
